import type {
  ContactSession,
  ContactThread,
  ConversationListItem,
  TakeoverState,
  TakeoverStateName,
  TranscriptMessage,
} from '@/services/conversationService';
import { mergeDeliveryInfo } from './deliveryStatus';

/**
 * Pure state updates for live WhatsApp events (livecenter contract §3) applied
 * to the Verläufe contact list and the open contact chat. No React, no I/O.
 *
 * Assignment is ONLY by `contact_key` (livecenter §7): the backend cuts a new
 * conversation row after a booking, so a `conversation_id` match would miss
 * the answer in the open chat (finding B2, 30.09.).
 */

/** `whatsapp.message` payload. */
export interface WhatsAppMessageEvent {
  conversation_id: number;
  contact_key?: string | null;
  customer_phone: string;
  customer_name?: string | null;
  agent_number: string | null;
  message: TranscriptMessage;
  last_message_at: string | null;
  status: string | null;
  is_test?: boolean;
  takeover?: TakeoverState | null;
}

export type TakeoverReason =
  | 'manual'
  | 'message'
  | 'released'
  | 'expired'
  | 'idle'
  | 'requested'
  | 'callback'
  | 'callback_resolved'
  | 'all_busy'
  | 'agent_resumed'
  | 'window_expired'
  | 'customer_waiting';

/** `whatsapp.takeover` payload. */
export interface WhatsAppTakeoverEvent {
  conversation_id?: number | null;
  contact_key?: string | null;
  customer_phone: string;
  agent_number: string | null;
  takeover: TakeoverState | null;
  by: string | null;
  reason: TakeoverReason | string;
}

/** `whatsapp.agent_typing` (ephemeral): the agent started or finished a turn. */
export interface AgentTypingEvent {
  contact_key: string;
  conversation_id?: number | null;
  active: boolean;
}

/** `whatsapp.staff_typing` (ephemeral): a colleague is typing in the web app. */
export interface StaffTypingEvent {
  contact_key: string;
  by: string | null;
  active: boolean;
}

/** A WhatsApp thread counts as "Live" while its last message is this recent. */
export const LIVE_WINDOW_MS = 10 * 60 * 1000;
/** Meta customer-service window: staff may reply up to 24 h after the customer. */
export const REPLY_WINDOW_MS = 24 * 60 * 60 * 1000;

function timeKey(ts?: string | null): string {
  if (!ts) return '';
  const t = Date.parse(ts);
  return Number.isNaN(t) ? ts : String(t);
}

/** Same row if role, text and timestamp agree (timestamps compared as instants). */
export function isSameMessage(a: TranscriptMessage, b: TranscriptMessage): boolean {
  return a.role === b.role && a.content === b.content && timeKey(a.ts) === timeKey(b.ts);
}

export function isSpokenMessage(message: TranscriptMessage): boolean {
  return message.role !== 'event';
}

export function replyWindowFrom(customerTs: string | null | undefined): string | null {
  if (!customerTs) return null;
  const t = Date.parse(customerTs);
  return Number.isNaN(t) ? null : new Date(t + REPLY_WINDOW_MS).toISOString();
}

// --- takeover ------------------------------------------------------------------

/** Current handoff state; an active takeover without `state` is an older `staff` payload. */
export function takeoverStateOf(takeover: TakeoverState | null | undefined): TakeoverStateName | null {
  if (!takeover?.active) return null;
  return takeover.state ?? 'staff';
}

/** The agent is silent (gates 1/2) for `requested` and `staff` — never for `callback`. */
export function isAgentPaused(takeover: TakeoverState | null | undefined): boolean {
  const state = takeoverStateOf(takeover);
  return state === 'requested' || state === 'staff';
}

/** Since when the contact waits for the salon, if it does. */
export function waitingSinceOf(takeover: TakeoverState | null | undefined): string | null {
  switch (takeoverStateOf(takeover)) {
    case 'requested':
      return takeover?.requested_at ?? takeover?.started_at ?? null;
    case 'callback':
      return takeover?.callback_at ?? takeover?.started_at ?? null;
    case 'staff':
      return takeover?.customer_waiting_since ?? null;
    default:
      return null;
  }
}

/**
 * Local takeover shown until the server answers. No deadline: the server owns
 * the timings (wait/idle minutes are per salon), so the ring stays hidden
 * instead of counting down a guessed value.
 */
export function optimisticTakeover(actor: string | null, now: number = Date.now()): TakeoverState {
  const startedAt = new Date(now).toISOString();
  return {
    active: true,
    state: 'staff',
    taken_by: actor,
    started_at: startedAt,
    expires_at: null,
    deadline_at: null,
    deadline_kind: null,
    last_staff_at: startedAt,
  };
}

// --- contact thread --------------------------------------------------------------

/**
 * `message` goes at the end unless the thread has that row already (stream
 * and POST answer deliver the same row). Then the row stays and only learns
 * the wamids/delivery the other copy brings — whichever came first, the row
 * must end up with them, or its ticks would never update.
 */
function withMessage(messages: TranscriptMessage[], message: TranscriptMessage): TranscriptMessage[] {
  const index = messages.findIndex((m) => isSameMessage(m, message));
  if (index === -1) return [...messages, message];
  const merged = mergeDeliveryInfo(messages[index], message);
  if (merged === messages[index]) return messages;
  const next = [...messages];
  next[index] = merged;
  return next;
}

function upsertSession(sessions: ContactSession[], event: WhatsAppMessageEvent): ContactSession[] {
  const ts = event.message.ts ?? event.last_message_at ?? null;
  const index = sessions.findIndex((s) => s.conversation_id === event.conversation_id);
  if (index === -1) {
    // A session the chat has not seen yet: the backend cut a new row (e.g.
    // after a booking). It is the newest one, so it goes last.
    const session: ContactSession = {
      conversation_id: event.conversation_id,
      started_at: ts,
      last_message_at: event.last_message_at ?? ts,
      status: event.status ?? null,
      summary: null,
      end_reason_text: null,
      is_test: !!event.is_test,
    };
    return [...sessions, session];
  }
  const current = sessions[index];
  const lastMessageAt = event.last_message_at ?? ts ?? current.last_message_at;
  const status = event.status ?? current.status;
  if (lastMessageAt === current.last_message_at && status === current.status) return sessions;
  const next = [...sessions];
  next[index] = { ...current, last_message_at: lastMessageAt, status };
  return next;
}

/** Apply `whatsapp.message` to the open contact chat. Idempotent (replays, POST + stream). */
export function applyMessageEventToThread(thread: ContactThread, event: WhatsAppMessageEvent): ContactThread {
  if (!event.contact_key || event.contact_key !== thread.contact_key) return thread;
  const message: TranscriptMessage = {
    ...event.message,
    conversation_id: event.message.conversation_id ?? event.conversation_id,
  };
  const known = thread.messages.some((m) => isSameMessage(m, message));
  const messages = withMessage(thread.messages, message);
  const sessions = upsertSession(thread.sessions, event);
  // Only a known state is adopted. `takeover: null` on a message event is not
  // proof that nothing is active (the agent-turn path does not look it up);
  // clearing is the job of `whatsapp.takeover` and of a refetch.
  const takeover = event.takeover ? event.takeover : thread.takeover;
  const customerName = event.customer_name?.trim() ? event.customer_name : thread.customer_name;

  const next: ContactThread = { ...thread, messages, sessions, takeover, customer_name: customerName };
  if (message.role === 'user' && !known) {
    const until = replyWindowFrom(message.ts ?? event.last_message_at);
    if (until) {
      next.reply_window_until = until;
      next.can_reply = true;
    }
  }
  return next;
}

export function applyTakeoverEventToThread(thread: ContactThread, event: WhatsAppTakeoverEvent): ContactThread {
  if (!event.contact_key || event.contact_key !== thread.contact_key) return thread;
  return { ...thread, takeover: event.takeover ?? null };
}

/** Result of `POST …/messages` (and anything else that appends one row). */
export function appendThreadMessage(thread: ContactThread, message: TranscriptMessage): ContactThread {
  const lastSession = thread.sessions[thread.sessions.length - 1];
  const withSession: TranscriptMessage = {
    ...message,
    conversation_id: message.conversation_id ?? lastSession?.conversation_id ?? null,
  };
  const messages = withMessage(thread.messages, withSession);
  return messages === thread.messages ? thread : { ...thread, messages };
}

/**
 * A page of older sessions (`before=`) goes in front of what is loaded. The
 * loaded (newer) state — takeover, reply window — stays; only `has_more`
 * comes from the older page.
 */
export function prependOlderSessions(thread: ContactThread, older: ContactThread): ContactThread {
  if (older.contact_key !== thread.contact_key) return thread;
  const loaded = new Set(thread.sessions.map((s) => s.conversation_id));
  const sessions = older.sessions.filter((s) => !loaded.has(s.conversation_id));
  if (sessions.length === 0) return { ...thread, has_more: older.has_more };
  const added = new Set(sessions.map((s) => s.conversation_id));
  const messages = older.messages.filter((m) => m.conversation_id != null && added.has(m.conversation_id));
  return {
    ...thread,
    sessions: [...sessions, ...thread.sessions],
    messages: [...messages, ...thread.messages],
    has_more: older.has_more,
  };
}

/**
 * A background refetch (reconnect, degraded polling) returns the newest page
 * only. Older sessions the reader already paged in stay in front of it, so
 * the chat does not jump; everything the fresh page covers is the server's.
 */
export function mergeThreadRefresh(current: ContactThread, fresh: ContactThread): ContactThread {
  if (current.contact_key !== fresh.contact_key || fresh.sessions.length === 0) return fresh;
  const oldestFresh = fresh.sessions[0].conversation_id;
  const freshIds = new Set(fresh.sessions.map((s) => s.conversation_id));
  const firstFreshIndex = current.sessions.findIndex((s) => s.conversation_id === oldestFresh);
  if (firstFreshIndex <= 0) return fresh;
  const kept = current.sessions.slice(0, firstFreshIndex).filter((s) => !freshIds.has(s.conversation_id));
  const keptIds = new Set(kept.map((s) => s.conversation_id));
  const keptMessages = current.messages.filter((m) => m.conversation_id != null && keptIds.has(m.conversation_id));
  return {
    ...fresh,
    sessions: [...kept, ...fresh.sessions],
    messages: [...keptMessages, ...fresh.messages],
    has_more: current.has_more,
  };
}

/**
 * Reply allowed right now: honours the server's `can_reply` (computed over all
 * sessions of the contact) and closes locally when `reply_window_until` passes.
 */
export function canReplyToThread(thread: ContactThread, now: number = Date.now()): boolean {
  if (!thread.can_reply) return false;
  if (thread.reply_window_until) {
    const until = Date.parse(thread.reply_window_until);
    if (!Number.isNaN(until)) return until > now;
  }
  return true;
}

// --- contact list ------------------------------------------------------------------

export interface ListUpdate {
  items: ConversationListItem[];
  /** False when the contact is not in the list (caller may reload). */
  known: boolean;
}

function listStateFields(takeover: TakeoverState | null | undefined) {
  const state = takeoverStateOf(takeover);
  return {
    takeover_state: state,
    takeover_active: state === 'staff',
    waiting_since: waitingSinceOf(takeover),
  };
}

function findContact(items: ConversationListItem[], contactKey: string | null | undefined): number {
  if (!contactKey) return -1;
  return items.findIndex((item) => item.type === 'whatsapp' && item.contact_key === contactKey);
}

/**
 * Update the contact's row and move it to the top. Not idempotent on its own
 * (it counts); the caller skips repeats with `SeenEventIds`.
 */
export function applyMessageEventToContactList(items: ConversationListItem[], event: WhatsAppMessageEvent): ListUpdate {
  const index = findContact(items, event.contact_key);
  if (index === -1) return { items, known: false };
  const item = items[index];
  const message = event.message;
  const spoken = isSpokenMessage(message);
  const updated: ConversationListItem = {
    ...item,
    // The row stands for the contact; its id follows the newest session.
    id: event.conversation_id,
    last_message_at: event.last_message_at ?? message.ts ?? item.last_message_at,
    status: event.status ?? item.status,
    // Same rule as the thread: a message event only ever SETS a state.
    ...(event.takeover ? listStateFields(event.takeover) : {}),
    ...(event.customer_name?.trim() ? { customer_name: event.customer_name } : {}),
    ...(spoken ? { preview: message.content, message_count: item.message_count + 1 } : {}),
    ...(message.role === 'user'
      ? { last_customer_message_at: message.ts ?? event.last_message_at ?? item.last_customer_message_at }
      : {}),
  };
  return { items: [updated, ...items.slice(0, index), ...items.slice(index + 1)], known: true };
}

export function applyTakeoverEventToContactList(
  items: ConversationListItem[],
  event: WhatsAppTakeoverEvent,
): ConversationListItem[] {
  const index = findContact(items, event.contact_key);
  if (index === -1) return items;
  const item = items[index];
  const fields = listStateFields(event.takeover);
  if (
    (item.takeover_state ?? null) === fields.takeover_state &&
    !!item.takeover_active === fields.takeover_active &&
    (item.waiting_since ?? null) === fields.waiting_since
  ) {
    return items;
  }
  const next = [...items];
  next[index] = { ...item, ...fields };
  return next;
}

// --- event id dedupe -----------------------------------------------------------------

/** How many applied event ids the Verläufe list remembers for deduplication. */
export const SEEN_EVENT_IDS_LIMIT = 500;

/**
 * Bounded memory of the SSE event ids already applied (oldest forgotten
 * first). `applyMessageEventToContactList` counts and re-sorts, so it is only
 * idempotent together with this guard.
 *
 * Consult it in the event handler, NOT inside a setState updater: StrictMode
 * runs updaters twice, and the second run would see its own id and drop the
 * update.
 */
export interface SeenEventIds {
  /** Remembers `id`; false when it was already applied. Events without an id always count as new. */
  markNew: (id: number | null | undefined) => boolean;
  /** `ready.last_id` below the highest id seen = ids restarted: forget them all. */
  noteServerCursor: (serverLastId: number) => void;
}

export function createSeenEventIds(limit: number = SEEN_EVENT_IDS_LIMIT): SeenEventIds {
  let ids = new Set<number>();
  let maxId = Number.NEGATIVE_INFINITY;
  return {
    markNew: (id) => {
      if (id == null) return true;
      if (ids.has(id)) return false;
      ids.add(id);
      maxId = Math.max(maxId, id);
      if (ids.size > limit) {
        const oldest = ids.values().next().value;
        if (oldest !== undefined) ids.delete(oldest);
      }
      return true;
    },
    noteServerCursor: (serverLastId) => {
      if (serverLastId >= maxId) return;
      ids = new Set<number>();
      maxId = Number.NEGATIVE_INFINITY;
    },
  };
}

// --- time ---------------------------------------------------------------------------------

/** How far a timestamp may lie in the future and still count as "now" (coarse
 *  UI ticks, a browser clock behind the server's). */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

/** "Live" indicator: last message younger than LIVE_WINDOW_MS. */
export function isRecentlyActive(lastMessageAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!lastMessageAt) return false;
  const t = Date.parse(lastMessageAt);
  if (Number.isNaN(t)) return false;
  const age = now - t;
  return age < LIVE_WINDOW_MS && age > -CLOCK_SKEW_TOLERANCE_MS;
}

export function formatClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
